import {CheckCircle, ErrorOutline, Info, WarningAmber} from '@mui/icons-material';
import {Alert, AlertProps, Box, Button, Slide, Typography, useTheme} from '@mui/material';
import {ReactNode} from 'react';
import toast, {Toast, ToastPosition} from 'react-hot-toast';
import {useTranslation} from 'tolgee';

import {ANNOTATOR_FULLSCREEN_TOASTER_ID} from '~constants/constants';
import {getAnnotatorFullscreenToasterId} from '~utils/annotatorFullscreen';
import {useDontShowAgain} from '~hooks/useDontShowAgain';

import {hotKeys} from '~components/images/annotator/utils/hotKeys';
import {useHotkeysWhenDialogsAreClosed} from 'src/hooks/misc.hooks';

interface ShowNotificationProps {
  title?: string;
  message: string | ReactNode;
  severity?: AlertProps['severity'];
  variant?: 'toast' | 'overlay';
  position?: ToastPosition;
  duration?: number;
  dontShowAgain?: string;
  id?: string;
  sx?: AlertProps['sx'];
  elevation?: number;
  direction?: 'left' | 'right' | 'up' | 'down';
}

interface NotificationProps extends ShowNotificationProps {
  onClose?: () => void;
  toastData?: Toast;
}

export function Notification({
  title,
  message,
  severity = 'info',
  variant = 'toast',
  dontShowAgain,
  onClose,
  toastData,
  sx,
  elevation,
  direction,
}: NotificationProps): JSX.Element | null {
  const {t} = useTranslation('common');
  const theme = useTheme();

  useHotkeysWhenDialogsAreClosed(hotKeys.closePopups.keyCombination, () => onClose && onClose());

  const {dismiss, shouldShow} = useDontShowAgain(dontShowAgain);

  const highlightColor = () => {
    switch (severity) {
      case 'success':
        return 'success.main';
      case 'info':
        return 'primary.light';
      case 'error':
        return 'error.light';
      case 'warning':
        return 'warning.light';
    }
  };

  const handleDontShowAgain = () => {
    if (toastData) toast.dismiss(toastData.id, toastData.toasterId);
    dismiss({delayMs: theme.transitions.duration.leavingScreen});
  };

  if (!shouldShow) {
    return null;
  }

  const getSlideDirection = () => {
    if (!toastData) return undefined;
    if (direction) return direction;
    return toastData.visible ? 'down' : 'left';
  };

  return (
    <Slide in={toastData ? toastData.visible : true} direction={getSlideDirection()} mountOnEnter unmountOnExit>
      {variant === 'overlay' ? (
        <Box
          data-testid='notification-overlay'
          sx={{
            position: 'fixed',
            bottom: theme.spacing(2),
            backgroundColor: 'rgba(51, 51, 51, 0.9)',
            color: 'maddox.globalwhite',
            padding: theme.spacing(1.25, 2),
            borderRadius: theme.spacing(1),
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            zIndex: theme.zIndex.snackbar,
            pointerEvents: 'none',
            boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.2)',
            maxWidth: 'min(720px, calc(100vw - 32px))',
            ...sx,
          }}
        >
          <Typography variant='body2' sx={{fontSize: '14px', lineHeight: 1.2}}>
            {message}
          </Typography>
        </Box>
      ) : (
        <Alert
          data-testid='notification-toast'
          sx={{width: '478px', overflow: 'hidden', wordBreak: 'break-word', ...sx}}
          onClose={onClose}
          severity={severity}
          elevation={elevation ?? 6}
          variant='filled'
          closeText={t('close')}
          iconMapping={{
            success: <CheckCircle sx={{color: highlightColor}} />,
            info: <Info sx={{color: highlightColor}} />,
            error: <ErrorOutline sx={{color: highlightColor}} />,
            warning: <WarningAmber sx={{color: highlightColor}} />,
          }}
        >
          {title && (
            <Typography variant='h3' sx={{color: 'maddox.black80', marginBottom: 1}}>
              {title}
            </Typography>
          )}
          <Typography variant='body1' sx={{color: 'maddox.black80'}}>
            {message}
          </Typography>
          {dontShowAgain && dontShowAgain.length > 0 && (
            <Button
              onClick={handleDontShowAgain}
              sx={{
                color: highlightColor,
                padding: 0,
                marginTop: 2,
                '&:hover': {backgroundColor: 'transparent', textDecoration: 'underline'},
              }}
            >
              {t('dontShowAgain')}
            </Button>
          )}
        </Alert>
      )}
    </Slide>
  );
}

/**
 * @returns An ID for the shown notification
 */
export const showNotification = ({
  title,
  message,
  severity,
  variant = 'toast',
  position,
  duration,
  direction,
  dontShowAgain,
  id,
  sx,
}: ShowNotificationProps): string => {
  if (!duration) {
    if (dontShowAgain !== undefined) {
      duration = 30000;
    } else if (severity === 'success' || severity === undefined) {
      duration = 3000;
    } else {
      duration = 5000;
    }
  }

  return toast.custom(
    (t) => (
      <Notification
        title={title}
        message={message}
        severity={severity}
        variant={variant}
        dontShowAgain={dontShowAgain}
        onClose={() => toast.dismiss(t.id, t.toasterId)}
        toastData={t}
        sx={sx}
        direction={direction}
      />
    ),
    {
      id,
      position: variant === 'overlay' ? 'bottom-center' : position || 'top-right',
      duration,
      toasterId: getAnnotatorFullscreenToasterId(),
    },
  );
};

export const hideNotification = (id: string) => {
  toast.dismiss(id);
  toast.dismiss(id, ANNOTATOR_FULLSCREEN_TOASTER_ID);
};
